import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  CircleNotch, ArrowLeft, Printer, CheckCircle, Circle,
  Lightbulb, Warning, ShieldCheck, Target, ListChecks, Users,
} from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ExportPdfButton from "@/components/ExportPdfButton";

const statusStyle = {
  Draft: "bg-slate-200 text-slate-700",
  "Active Execution": "bg-blue-100 text-blue-700",
  Completed: "bg-emerald-100 text-emerald-700",
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-blue-700" />
      <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-700">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const ReadField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-400 mb-1">{label}</p>
    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{value || <span className="text-slate-300">—</span>}</p>
  </div>
);

export default function Tracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get(`/documents/${id}`);
      setA(res.data);
    } catch {
      toast.error("Assessment not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const toggle = async (groupId, taskId, current) => {
    // optimistic update
    setA((prev) => {
      const payload = { ...prev.payload };
      const armor = { ...payload.armor };
      armor.workingGroups = armor.workingGroups.map((g) =>
        g.id === groupId ? { ...g, tasks: g.tasks.map((t) => (t.id === taskId ? { ...t, status: !current } : t)) } : g
      );
      payload.armor = armor;
      return { ...prev, payload };
    });
    try {
      const res = await api.patch(`/documents/${id}/task`, { groupId, taskId, status: !current });
      setA((prev) => ({ ...prev, status: res.data.status, progress: res.data.progress }));
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  if (loading || !a)
    return (
      <Layout>
        <div className="flex items-center justify-center py-40 text-slate-400">
          <CircleNotch size={28} className="animate-spin" />
        </div>
      </Layout>
    );

  const f = a.payload?.focus || {};
  const arm = a.payload?.armor || {};
  const gk = f.gatekeepers || {};

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10 print:px-0 print:py-0 print:max-w-none">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 print:block">
          <div>
            <button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-2 print:hidden">
              <ArrowLeft size={16} /> Dashboard
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{a.title}</h1>
              <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${statusStyle[a.status]}`}>
                {a.status === "Active Execution" ? "Active Execution" : a.status}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <ShareButton docId={id} initialToken={a.share_token} />
            <ExportPdfButton docId={id} />
            <button data-testid="export-pdf-button" onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-sm bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800">
              <Printer size={18} /> Export to PDF / Print
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-5 bg-white border border-slate-200 rounded-sm shadow-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">Execution Progress</span>
            <span className="font-mono text-sm font-semibold text-slate-700" data-testid="progress-percent">
              {a.progress.done}/{a.progress.total} · {a.progress.pct}%
            </span>
          </div>
          <div className="h-3 bg-slate-100 rounded-sm overflow-hidden">
            <div className={`h-full transition-all duration-500 ${a.progress.pct === 100 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${a.progress.pct}%` }} />
          </div>
        </div>

        {/* FOCUS read-only */}
        <div className="mt-6">
          <h2 className="text-xs font-bold tracking-[0.12em] uppercase text-blue-700 mb-3">Phase 1 · FOCUS — Strategic Decisions</h2>
          <div className="grid grid-cols-1 gap-4">
            <Section title="Context & Objectives" icon={Target}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ReadField label="Fact-Find & Context" value={f.factFind} />
                <ReadField label="Strategic Intent" value={f.strategicIntent} />
                <ReadField label="Operational Objectives" value={f.operationalObjectives} />
                <ReadField label="Selected Course of Action" value={f.selectedOption} />
              </div>
            </Section>

            {(f.coas || []).some((c) => c) && (
              <Section title="Options & Risk Evaluation" icon={Warning}>
                <div className="space-y-3">
                  {(f.coas || []).map((c, i) =>
                    c ? (
                      <div key={i} className="border border-slate-200 rounded-sm overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                          <span className="font-mono text-xs font-semibold text-slate-600">COA {i + 1}</span>
                          <span className="ml-2 text-sm text-slate-700">{c}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 text-sm">
                          <div className="p-3 flex gap-2"><Lightbulb size={16} className="text-emerald-600 shrink-0 mt-0.5" /><span className="text-slate-700 whitespace-pre-wrap">{f.risks?.[i]?.opportunity || "—"}</span></div>
                          <div className="p-3 flex gap-2"><Warning size={16} className="text-red-600 shrink-0 mt-0.5" /><span className="text-slate-700 whitespace-pre-wrap">{f.risks?.[i]?.risk || "—"}</span></div>
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              </Section>
            )}

            <Section title="Decision Gatekeepers" icon={ShieldCheck}>
              <div className="flex flex-wrap gap-3">
                <Gate ok={gk.valueAlignment} label="Value Alignment" />
                <Gate ok={gk.objectiveCertainty} label="Objective Certainty" />
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-slate-200 bg-slate-50">
                  <span className="text-sm font-medium text-slate-700">Authority:</span>
                  <span className="font-mono text-xs font-semibold text-slate-900">{gk.authorityBaseline || "—"}</span>
                </div>
              </div>
            </Section>
          </div>
        </div>

        {/* ARMOR checklist */}
        <div className="mt-8">
          <h2 className="text-xs font-bold tracking-[0.12em] uppercase text-amber-700 mb-3">Phase 2 · ARMOR — Execution Checklist</h2>

          {(arm.alignment || arm.phases) && (
            <Section title="Alignment & Method" icon={Target}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ReadField label="Alignment & Objective" value={arm.alignment} />
                <ReadField label="High-Level Phases" value={arm.phases} />
              </div>
            </Section>
          )}

          <div className="space-y-4 mt-4">
            {(arm.workingGroups || []).length === 0 && (
              <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-6 text-center">No working groups defined.</p>
            )}
            {(arm.workingGroups || []).map((g) => {
              const total = g.tasks.length;
              const done = g.tasks.filter((t) => t.status).length;
              return (
                <div key={g.id} className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid={`group-${g.id}`}>
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users size={18} weight="duotone" className="text-slate-700" />
                      <h3 className="text-sm font-semibold text-slate-900">{g.name || "Working Group"}</h3>
                    </div>
                    <span className="font-mono text-xs text-slate-500">{done}/{total}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {g.tasks.map((t) => (
                      <button key={t.id} data-testid={`task-${t.id}`} onClick={() => toggle(g.id, t.id, t.status)}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition-colors print:hover:bg-white">
                        {t.status ? (
                          <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" />
                        ) : (
                          <Circle size={22} className="text-slate-300 shrink-0" />
                        )}
                        <span className={`text-sm ${t.status ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.name || "Untitled task"}</span>
                      </button>
                    ))}
                    {g.tasks.length === 0 && <p className="px-5 py-3 text-sm text-slate-400">No tasks assigned.</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Boundaries + resources */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {(arm.workingGroups || []).length > 0 && (
              <Section title="Operational Boundaries" icon={ListChecks}>
                <div className="space-y-3">
                  {(arm.workingGroups || []).map((g, gi) => (
                    <div key={g.id} className="text-sm">
                      <p className="font-mono text-xs font-semibold text-slate-600">{g.name || `Group ${gi + 1}`}</p>
                      <p className="text-emerald-700 mt-0.5"><span className="font-medium">Freedoms:</span> {arm.boundaries?.[gi]?.freedoms || "—"}</p>
                      <p className="text-red-700 mt-0.5"><span className="font-medium">Constraints:</span> {arm.boundaries?.[gi]?.constraints || "—"}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Milestones & Resources" icon={Target}>
              <div className="space-y-3">
                <ReadField label="Hard Deadlines" value={arm.milestones?.deadlines} />
                <ReadField label="Key Milestones" value={arm.milestones?.milestones} />
                <ReadField label="Review Cadence" value={arm.milestones?.reviewCadence} />
                <ReadField label="Internal Resources" value={arm.resources?.internal} />
                <ReadField label="External SMEs" value={arm.resources?.externalSMEs} />
                <ReadField label="Tracked KPIs" value={arm.resources?.kpis} />
              </div>
            </Section>
          </div>
        </div>

        <div className="hidden print:block mt-8 pt-4 border-t border-slate-300 font-mono text-xs text-slate-400">
          FOCUS·ARMOR Operational Plan · {a.title} · Generated {new Date().toLocaleString()}
        </div>
      </div>
    </Layout>
  );
}

const Gate = ({ ok, label }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border ${ok ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
    {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <Circle size={18} className="text-slate-400" />}
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <span className={`font-mono text-xs font-semibold ${ok ? "text-emerald-700" : "text-slate-500"}`}>{ok ? "YES" : "NO"}</span>
  </div>
);
